import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { DnsRecordDto, DnsZoneRecordsResponse } from '@jordan/types';
import { Alert, Breadcrumb, Button, DataTable, type DataTableColumn, Input, Modal, PageHeader, SelectMenu, Spinner, Textarea } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiDelete, apiGet, apiPost } from '../../lib/api';
import { cloudSidebar } from './cloudSidebar';

const TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA', 'PTR'];
const PROVIDER_LABEL: Record<string, string> = { gcp: 'Google Cloud', aws: 'AWS' };

interface RecForm {
  name: string;
  type: string;
  ttl: string;
  values: string; // one per line
}

export function CloudDnsZonePage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { provider = '', zoneId = '' } = useParams();

  const [data, setData] = useState<DnsZoneRecordsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState<DnsRecordDto | 'new' | null>(null);
  const [form, setForm] = useState<RecForm>({ name: '', type: 'A', ttl: '300', values: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    apiGet<DnsZoneRecordsResponse>(`/api/cloud/dns/zones/${provider}/${encodeURIComponent(zoneId)}/records`)
      .then(setData)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'Could not load records.'))
      .finally(() => setLoading(false));
  }, [provider, zoneId]);
  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing('new');
    setForm({ name: '', type: 'A', ttl: '300', values: '' });
  };
  const openEdit = (r: DnsRecordDto) => {
    setEditing(r);
    setForm({ name: r.name, type: r.type, ttl: r.ttl == null ? '300' : String(r.ttl), values: r.values.join('\n') });
  };
  const close = () => setEditing(null);

  const save = async () => {
    const values = form.values.split('\n').map((v) => v.trim()).filter(Boolean);
    if (values.length === 0) {
      setErr('Enter at least one record value.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiPost(`/api/cloud/dns/zones/${provider}/${encodeURIComponent(zoneId)}/records`, {
        name: form.name,
        type: form.type,
        ttl: Number(form.ttl) || 300,
        values,
      });
      close();
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save the record.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: DnsRecordDto) => {
    if (!window.confirm(`Delete ${r.type} record ${r.name}?`)) return;
    setErr(null);
    try {
      await apiDelete(
        `/api/cloud/dns/zones/${provider}/${encodeURIComponent(zoneId)}/records?name=${encodeURIComponent(r.name)}&type=${r.type}`,
      );
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not delete the record.');
    }
  };

  const isAlias = (r: DnsRecordDto) => r.values.some((v) => v.endsWith('(alias)'));

  const columns: DataTableColumn<DnsRecordDto>[] = [
    { key: 'name', header: 'Name', sortable: true, render: (r) => <span className="fw-semibold">{r.name}</span> },
    { key: 'type', header: 'Type', sortable: true, render: (r) => <span className="badge bg-light text-body border">{r.type}</span> },
    { key: 'ttl', header: 'TTL', align: 'end', sortable: true, sortValue: (r) => r.ttl ?? -1, render: (r) => (r.ttl == null ? '—' : r.ttl) },
    {
      key: 'values',
      header: 'Value',
      render: (r) => (
        <div className="d-flex flex-column">
          {r.values.map((v, i) => (
            <span key={i} className="font-monospace text-truncate" style={{ maxWidth: 460 }}>
              {v}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (r) => (
        <div className="d-flex justify-content-end gap-1">
          <button
            className="btn btn-light btn-icon btn-sm rounded-pill border-transparent"
            title={isAlias(r) ? 'Alias records can’t be edited here' : 'Edit'}
            disabled={isAlias(r)}
            onClick={() => openEdit(r)}
          >
            <i className="ph-pencil" />
          </button>
          <button
            className="btn btn-light btn-icon btn-sm rounded-pill border-transparent text-danger"
            title="Delete"
            onClick={() => void remove(r)}
          >
            <i className="ph-trash" />
          </button>
        </div>
      ),
    },
  ];

  const zoneName = data?.zoneName ?? zoneId;

  return (
    <AppChrome secondarySidebar={cloudSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Cloud DNS - <span className="fw-normal">{zoneName}</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Cloud Management' },
              { label: 'Cloud DNS', href: '/cloud/dns' },
              { label: zoneName },
            ]}
          />
        }
        actions={
          <div className="d-flex gap-2">
            <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={load}>
              Refresh
            </Button>
            <Button variant="primary" icon="ph-plus" onClick={openNew}>
              Add record
            </Button>
          </div>
        }
      />

      <div className="content">
        <div className="d-flex align-items-center mb-3 text-muted">
          <i className="ph-globe-hemisphere-west me-2" />
          Served through <span className="fw-semibold mx-1">{PROVIDER_LABEL[provider] ?? provider}</span>
        </div>

        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {data === null && loading ? (
          <Spinner />
        ) : data ? (
          <div className="card border-0 shadow-sm">
            <DataTable
              columns={columns}
              rows={data.records}
              getRowKey={(r) => r.id}
              searchable
              searchPlaceholder="Search records…"
              initialSort={{ key: 'name', dir: 'asc' }}
              pageSize={50}
              emptyMessage="No records in this zone yet."
            />
          </div>
        ) : null}
      </div>

      {editing && (
        <Modal
          open
          onClose={close}
          title={editing === 'new' ? 'Add DNS record' : `Edit ${(editing as DnsRecordDto).type} record`}
          footer={
            <>
              <Button variant="light" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void save()}>
                {editing === 'new' ? 'Add record' : 'Save changes'}
              </Button>
            </>
          }
        >
          <div className="row">
            <div className="col-8">
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={`@ or sub.${zoneName}`}
                hint={`Use @ for the apex (${zoneName}). A subdomain may be relative or fully-qualified.`}
              />
            </div>
            <div className="col-4">
              <SelectMenu
                label="Type"
                icon="ph-tag"
                value={form.type}
                onChange={(v) => setForm((f) => ({ ...f, type: v }))}
                options={TYPES.map((t) => ({ value: t, label: t }))}
              />
            </div>
            <div className="col-4">
              <Input
                label="TTL (seconds)"
                type="number"
                min={0}
                value={form.ttl}
                onChange={(e) => setForm((f) => ({ ...f, ttl: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <Textarea
                label="Value(s)"
                rows={3}
                value={form.values}
                onChange={(e) => setForm((f) => ({ ...f, values: e.target.value }))}
                hint="One value per line (e.g. an IP for A, a hostname for CNAME)."
              />
            </div>
          </div>
        </Modal>
      )}
    </AppChrome>
  );
}
